import { useState, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { 
  ZoneGeometry, 
  ZoneInput, 
  ZoneType,
  PolygonGeometry,
      
  useCreateZoneMutation 
} from "@/api/v0/zones";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { ZoneDraw } from "@/components/map/zone-draw";
import { ZoneForm } from "@/components/zones/zone-form";
import { toast } from "sonner";
import { Map } from "react-map-gl/maplibre";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useStore } from "@/store/store";

export function ZoneCreateRoute() {
  const navigate = useNavigate();
  const [zoneGeometry, setZoneGeometry] = useState<ZoneGeometry>({
    type: "polygon",
    coordinates: []
  } as PolygonGeometry);
  const [zoneType, setZoneType] = useState<ZoneType>("polygon");
  const [step, setStep] = useState<"draw" | "details">("draw");

  const mapStyleUrl = useStore((state) => state.mapStyleUrl);
  const initialViewState = useStore((state) => state.mapBounds);
  const zoom = useStore((state) => state.mapZoom);
  
  const createZoneMutation = useCreateZoneMutation();

  const handleZoneDrawn = useCallback((geometry: ZoneGeometry) => {
    setZoneGeometry(geometry);
    setZoneType('polygon');
  }, []);

  const handleContinue = () => {
    // Validate that a zone has been drawn
    const polygonGeometry = zoneGeometry as PolygonGeometry;
    if (!polygonGeometry.coordinates || 
        polygonGeometry.coordinates.length === 0 || 
        polygonGeometry.coordinates[0].length < 4) { // At least 3 points + closing point
      toast.error("Please draw a valid polygon with at least 3 points");
      return;
    }
    
    setStep("details");
  };

  const handleSubmit = async (data: ZoneInput) => {
    try {
      await createZoneMutation.mutateAsync(data);
      toast.success("Zone created successfully");
      navigate({ to: "/zones" as any });
    } catch (error) {
      toast.error("Failed to create zone");
      console.error("Create zone error:", error);
    }
  };

  return (
    <main className="w-full h-full flex flex-col ">
      <div className="p-4 border-b flex items-center gap-4">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => navigate({ to: "/zones" as any })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Create Zone</h1>
      </div>

      {step === "draw" ? (
        <div className="flex-1 flex flex-col min-w-6xl">
          <div className="p-4 pb-2">
            <h2 className="text-xl font-semibold">Create a Zone</h2>
            <p className="text-sm text-muted-foreground">
              Draw an area on the map that you want to monitor.
            </p>
          </div>
          
          <div className="flex-1 relative">
            <Map
              id="zone-map"
              mapStyle={mapStyleUrl}
              initialViewState={{
                longitude: initialViewState[0],
                latitude: initialViewState[1],
                zoom: zoom as number
              }}
              style={{ width: '100%', height: '100%' }}
              mapLib={import('maplibre-gl')}
              attributionControl={false}
              reuseMaps
            >
              <ZoneDraw onZoneDrawn={handleZoneDrawn} />
            </Map>
          </div>
          
          <div className="p-4 border-t flex justify-end">
            <Button 
              onClick={handleContinue}
              disabled={
                !zoneGeometry || 
                !(zoneGeometry as PolygonGeometry).coordinates || 
                (zoneGeometry as PolygonGeometry).coordinates.length === 0 || 
                (zoneGeometry as PolygonGeometry).coordinates[0].length < 4
              }
            >
              Continue to Zone Details
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 p-4 overflow-auto">
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Zone Details</CardTitle>
                <CardDescription>
                  Provide information about this {zoneType} zone
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground">
                    Zone Type: <strong>{zoneType}</strong>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Geometry Type: <strong>{zoneGeometry.type}</strong>
                  </p>
                </div>
                <ZoneForm 
                  onSubmit={handleSubmit} 
                  zoneGeometry={zoneGeometry}
                  geometryType={zoneType}
                  isSubmitting={createZoneMutation.isPending}
                />
                <div className="mt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setStep("draw")}
                    className="mr-2"
                  >
                    Back to Drawing
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </main>
  );
} 